import { useState, useEffect } from 'react';
import FormInputField from '../Global/FormInputField';
import useApiServices from '../../Utils/useApiService';
import { ACC_NUM_IFSC_VERIFY_API, IFSC_VERIFY_API, UPDATE_VENDER_DETAILS } from '../../Utils/Constants';
import swal from 'sweetalert';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { closeModal } from '../../Utils/handleModal';

const EditVendorBankDetails = ({ data }) => {
  const [formData, setFormData] = useState({
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    bankName: '',
    branchName: '',
    remark: '',
  });

  const [errors, setErrors] = useState({
    accountMismatch: false,
    ifscError: '',
    bankVerifyError: '',
  });

  const [isIfscVerified, setIsIfscVerified] = useState(true); // true | false | "NotVerified"
  const [isLoading, setIsLoading] = useState(false);

  const { apiCallFnforPostForOpenUrl, apiCallFnforPut } = useApiServices();

  useEffect(() => {
    if (data) setFormData({
      accountNumber: data?.accountNumber,
      confirmAccountNumber: data?.accountNumber,
      ifscCode: data?.ifscCode,
      bankName: data?.bankName,
      branchName: data?.branchName
    });
  }, [data]);

  const handleChange = (e) => {
    try {
      const { name, value } = e.target;

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Clear errors on typing
      if (name === 'confirmAccountNumber' || name === 'accountNumber') {
        setErrors((prev) => ({ ...prev, accountMismatch: false }));
      }
      if (name === 'ifscCode') {
        setErrors((prev) => ({ ...prev, ifscError: '', bankVerifyError: '' }));
        setIsIfscVerified(null);
      }
    } catch (error) {
      console.log(`ERROR WHILE CHANGING INPUTS`, error);
    }
  };

  const handleBlur = () => {
    try {
      if (
        formData?.accountNumber &&
        formData?.confirmAccountNumber &&
        formData?.accountNumber !== formData.confirmAccountNumber
      ) {
        setErrors((prev) => ({ ...prev, accountMismatch: true }));
      }
    } catch (error) {
      console.log(`ERROR WHILE CHECKING INPUTS`, error);
    }
  };

  const verifyIfsc = async () => {
    const { ifscCode, accountNumber } = formData;
    if (!ifscCode) {
      return setErrors((prev) => ({ ...prev, ifscError: '*Please Enter IFSC Code' }));
    }

    if (ifscCode.length !== 11) {
      return setErrors((prev) => ({ ...prev, ifscError: '*Please Enter Valid IFSC Code' }));
    }

    if (!accountNumber) {
      return setErrors((prev) => ({ ...prev, accountError: '*Please Enter Account Number' }));
    }

    try {
      setIsLoading(true);
      setErrors({ accountMismatch: false, ifscError: '', bankVerifyError: '' });

      const isAccVerified = await apiCallFnforPostForOpenUrl(ACC_NUM_IFSC_VERIFY_API, {
        ifsc: ifscCode,
        accountNumber,
      });

      const res = await apiCallFnforPostForOpenUrl(IFSC_VERIFY_API, { ifscCode });

      if (res?.statusCode === 200 && isAccVerified?.statusCode === 200) {
        setFormData((prev) => ({
          ...prev,
          bankName: res?.data?.bank,
          branchName: res?.data?.branch,
        }));
        setIsIfscVerified(true);
      } else {
        setIsIfscVerified("NotVerified");
        swal("Alert!", "Invalid Bank Details!", "error");
      }
    } catch (error) {
      console.error("Error verifying IFSC:", error);
      swal("Error", "Something went wrong during verification.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    const { accountNumber, confirmAccountNumber, ifscCode, bankName, branchName, remark } = formData;

    // Reset errors
    setErrors({ accountMismatch: false, ifscError: '', bankVerifyError: '' });

    // Validation
    if (!accountNumber || !confirmAccountNumber || !ifscCode || !bankName || !branchName) {
      swal("Error", "Please fill in all required fields.", "error");
      return;
    }

    if (accountNumber !== confirmAccountNumber) {
      setErrors((prev) => ({ ...prev, accountMismatch: true }));
      swal("Error", "Account numbers do not match.", "error");
      return;
    }

    if (ifscCode.length !== 11) {
      setErrors((prev) => ({ ...prev, ifscError: "*Please Enter Valid IFSC Code" }));
      swal("Error", "Invalid IFSC code.", "error");
      return;
    }

    // by pass for now 
    // if (isIfscVerified !== true) {
    //   swal("Error", "Please verify your IFSC Code before submitting.", "error");
    //   return;
    // }

    // Final API call
    try {
      setIsLoading(true);
      const payload = {
        accountNumber,
        ifscCode,
        bankName,
        branchName,
        email: data?.email || "",
        upi: data?.upi || "",
        vendorId: data?.vendorId || "",
        // vendorStatus: data?.vendorStatus || "",
        mobile: data?.mobile || "",
        remark: ""
      };

      const response = await apiCallFnforPut(UPDATE_VENDER_DETAILS, payload);
      if (response?.statusCode === 200) {
        swal("Success", "Bank details updated successfully!", "success");
        setFormData({});
        // ✅ Close the right modal
        closeModal("editVendorBankDetails");
      } else {
        swal("Error", response?.message || "Something went wrong.", "error");
      }
    } catch (error) {
      const errMessage = error?.response?.data?.message || error?.response?.message || error?.message || "Failed to update bank details.";
      swal("Error", errMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="row">
        <FormInputField
          label="Account Number"
          required
          type="number"
          name="accountNumber"
          value={formData.accountNumber}
          placeholder="Account Number"
          onChange={handleChange}
          errorText={errors?.accountError}
        />

        <FormInputField
          label="Confirm Account Number"
          required
          type="number"
          name="confirmAccountNumber"
          value={formData.confirmAccountNumber}
          placeholder="Confirm Account Number"
          onChange={handleChange}
          onBlur={handleBlur}
          errorText={errors?.accountMismatch ? '*Account Number Mismatch!' : ''}
        />

        <FormInputField
          label="IFSC Code"
          required
          name="ifscCode"
          value={formData.ifscCode}
          placeholder="IFSC Code"
          onChange={handleChange}
          errorText={errors?.ifscError}
          extraRightElement={
            <>
              {errors.bankVerifyError && (
                <span className="text-danger">{errors.bankVerifyError}</span>
              )}

              <div className="d-flex justify-content-end w-100">
                {isIfscVerified === true ? (
                  <span className="text-success mt-1">
                    <b><DoneAllIcon fontSize="small" /> Verified</b>
                  </span>
                ) : isIfscVerified === "NotVerified" ? (
                  <span className="text-danger mt-1">
                    <b><HighlightOffIcon fontSize="small" /> Not Verified</b>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn btn-link p-0 mt-1"
                    onClick={verifyIfsc}
                    disabled={isLoading}
                  >
                    {isLoading ? "Verifying..." : "Verify IFSC"}
                  </button>
                )}
              </div>
            </>
          }
        />

        <FormInputField
          label="Bank Name"
          required
          name="bankName"
          value={formData.bankName}
          placeholder="Bank Name"
          onChange={handleChange}
        />

        <FormInputField
          label="Branch Name"
          required
          name="branchName"
          value={formData.branchName}
          placeholder="Branch Name"
          onChange={handleChange}
        />
        <FormInputField
          label="Remark"
          required={false}
          name="Remark"
          value={formData?.remark}
          placeholder="Remark"
          onChange={handleChange}
        />
      </div>

      <div className="col">
        <div className="form-group d-flex justify-content-end gap-3">
          <div className="login-btn-sec toolbar-bt text-end">
            <button
              id="registerVender"
              className="advanced-btn btn btn-primary btn-sm"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditVendorBankDetails;